/**
 * Catalog Client
 * Provides contract-based access to catalog/modules endpoints.
 * Follows Multi-Endpoint Pattern from client factory architecture.
 * @module api/clients/catalog
 */
import api from "@/services/api"
import { createContractClient, contractRequest } from "../contractClient"
import {
  ModulesListAnyResponseSchema,
  ModuleFieldsResponseSchema,
  ModuleItemsResponseSchema,
  ModuleItemResponseSchema,
  ModuleItemOperationResponseSchema,
  ModuleDeleteResponseSchema,
  ModuleToggleResponseSchema,
  ModuleDuplicateResponseSchema,
} from "../contracts/modules"
import { CustomFilledBoxesResponseSchema } from "../contracts/custom"
import {
  normalizeModulesList,
  normalizeModuleFields,
  normalizeModuleFilterFields,
  normalizeModuleItems,
  normalizeModuleItem,
  normalizeDeleteResponse,
  normalizeToggleResponse,
  normalizeDuplicateResponse,
  normalizeFilledBoxes,
  normalizeLpCdListRowDisplayFields,
  normalizeModuleItemForSavePayload,
  normalizeModuleItemForEditPayload,
} from "../normalizers/catalog.normalizer"

// LLM:METADATA
// :hierarchy: [Domain:Catalog | API_Client | Factory]
// :relates-to: uses: "createContractClient", "contractRequest", uses: "catalog normalizers", required-by: "CatalogModules.vue", "CatalogItems.vue"
// :rationale: "Provides catalog modules API calls with response normalization for 15 catalog endpoints (modules CRUD, items CRUD, bulk operations, module management). Multi-Endpoint pattern required due to different base paths: catalog/modules (module-level operations) and catalog/modules/{id}/items (item-level operations). Schema validation omitted for arrays due to z.array() passthrough limitation. Uses contractRequest directly for dynamic endpoints to avoid undefined base path issues."
// :contract: pre: "api client initialized, user authenticated", post: "Client object with catalog modules/items methods", invariant: "All responses normalized and validated"
// :decision_cache: "Chose Multi-Endpoint pattern for catalog client due to complex resource hierarchy (modules -> items). Dynamic apiUrl construction needed for item operations that depend on module ID. Uses contractRequest directly for dynamic endpoints (fields, items) instead of createContractClient to avoid 'undefined' prefix in endpoint paths. Schema validation used for single item responses, normalization-only for array endpoints [CATALOG-CLIENT-001]"
// :references: SPEC: "Phase 3.5 Catalog Client Implementation", ARCH: "client-factory-architecture.md Pattern 3"
// LLM:END

const isPlainObject = (value) => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false
  }

  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

const normalizePositiveSafeItemId = (itemId) => {
  if (Number.isSafeInteger(itemId) && itemId > 0) return itemId

  if (typeof itemId === "string" && /^[1-9]\d*$/.test(itemId)) {
    const normalizedItemId = Number(itemId)
    if (Number.isSafeInteger(normalizedItemId) && normalizedItemId > 0) {
      return normalizedItemId
    }
  }

  throw new Error("Module item ID must be a positive safe integer")
}

const normalizePositiveFolderId = (folderId) => {
  if (typeof folderId === "number") {
    return Number.isSafeInteger(folderId) && folderId > 0
      ? String(folderId)
      : null
  }

  if (typeof folderId !== "string") return null

  const trimmedFolderId = folderId.trim()
  if (!/^\d+$/.test(trimmedFolderId)) return null

  const numericFolderId = Number(trimmedFolderId)
  return Number.isSafeInteger(numericFolderId) && numericFolderId > 0
    ? String(numericFolderId)
    : null
}

const isExistingNumericLink = (itemData) => {
  if (!isPlainObject(itemData)) return false
  // A canonical create/clone payload carries clone_id and must never be treated as
  // a link to an existing item, even if a source identity key leaked into the payload.
  if (Object.prototype.hasOwnProperty.call(itemData, "clone_id")) return false
  try {
    normalizePositiveSafeItemId(itemData.id)
    return true
  } catch {
    return false
  }
}

const isNewLinkedItem = (itemData) =>
  isPlainObject(itemData) && (itemData.id === 0 || itemData.id === "0")

const getResponseHeader = (response, name) => {
  const headers = response?.headers
  if (headers && typeof headers.get === "function") {
    return headers.get(name)
  }
  if (!headers || typeof headers !== "object") return undefined
  return headers[name] || headers[name.toLowerCase()]
}

const isCsvContentType = (contentType) =>
  /^text\/csv(?:\s*;|$)/i.test(String(contentType || "").trim())

const getExportFilename = (contentDisposition, moduleId) => {
  const match = String(contentDisposition || "").match(
    /filename\*?\s*=\s*(?:UTF-8''|"?)([^";]+)"?/i
  )
  const filename = match?.[1] ? decodeURIComponent(match[1].trim()) : ""
  if (
    filename &&
    !/[\\/\u0000-\u001F\u007F]/.test(filename) &&
    /\.csv$/i.test(filename)
  ) {
    return filename
  }
  return `module_${moduleId}_export.csv`
}

const PUBLICATION_RESPONSE_KEYS = [
  "result",
  "outcome",
  "operation_id",
  "msg",
  "code",
  "barcode",
  "error",
]
const PUBLICATION_OUTCOMES = [
  "succeeded",
  "reconciled",
  "rejected",
  "not_sent",
  "indeterminate",
]
const PUBLICATION_SUCCESS_OUTCOMES = ["succeeded", "reconciled"]
const PUBLICATION_FAILURE_OUTCOMES = ["rejected", "not_sent", "indeterminate"]
const PUBLICATION_ERROR_MESSAGE_LIMIT = 256

export class OneCProductPublicationError extends Error {
  constructor({ message, outcome, operationId, kind, details }) {
    super(message)
    this.outcome = outcome
    this.operationId = operationId
    this.kind = kind
    this.details = details
  }
}

const boundedPublicationMessage = (value, fallback) => {
  const message = typeof value === "string" ? value.trim() : ""
  return (message || fallback).slice(0, PUBLICATION_ERROR_MESSAGE_LIMIT)
}

const publicationOperationId = (data) =>
  typeof data?.operation_id === "string" ? data.operation_id.trim() : null

const publicationProtocolError = (operation, data, trustedOperationId = null) =>
  new OneCProductPublicationError({
    message: boundedPublicationMessage(
      null,
      `Invalid 1C ${operation} publication response`
    ),
    outcome: "indeterminate",
    operationId: trustedOperationId ?? publicationOperationId(data),
    kind: "protocol",
    details: null,
  })

const publicationTransportError = (operation, operationId, error) =>
  new OneCProductPublicationError({
    message: boundedPublicationMessage(
      error?.message,
      `1C ${operation} publication transport failed`
    ),
    outcome: "indeterminate",
    operationId,
    kind: "transport",
    details: null,
  })

const isValidPublicationError = (error) =>
  error === null ||
  (isPlainObject(error) &&
    typeof error.kind === "string" &&
    Boolean(error.kind.trim()) &&
    typeof error.message === "string" &&
    Boolean(error.message.trim()) &&
    Object.prototype.hasOwnProperty.call(error, "details"))

const isCanonicalPublicationResponse = (data) => {
  if (!isPlainObject(data)) return false
  const keys = Object.keys(data).sort()
  const expectedKeys = [...PUBLICATION_RESPONSE_KEYS].sort()
  if (
    keys.length !== expectedKeys.length ||
    keys.some((key, index) => key !== expectedKeys[index])
  ) {
    return false
  }
  return (
    (data.result === 0 || data.result === 1) &&
    PUBLICATION_OUTCOMES.includes(data.outcome) &&
    typeof data.operation_id === "string" &&
    typeof data.msg === "string" &&
    typeof data.code === "string" &&
    typeof data.barcode === "string" &&
    isValidPublicationError(data.error)
  )
}

const normalizePublicationResponse = (
  data,
  operation,
  expectedOperationId = null
) => {
  if (!isCanonicalPublicationResponse(data)) {
    throw publicationProtocolError(operation, data, expectedOperationId)
  }

  const operationId = data.operation_id.trim()
  if (expectedOperationId !== null && operationId !== expectedOperationId) {
    throw publicationProtocolError(operation, data, expectedOperationId)
  }

  const code = data.code.trim()
  const barcode = data.barcode.trim()
  if (
    data.result === 1 &&
    PUBLICATION_SUCCESS_OUTCOMES.includes(data.outcome) &&
    operationId &&
    code &&
    barcode &&
    data.error === null
  ) {
    return { operationId, code, barcode }
  }

  if (
    data.result === 0 &&
    PUBLICATION_FAILURE_OUTCOMES.includes(data.outcome) &&
    !code
  ) {
    const error = data.error
    throw new OneCProductPublicationError({
      message: boundedPublicationMessage(
        error?.message,
        boundedPublicationMessage(
          data.msg,
          `1C ${operation} publication was not successful`
        )
      ),
      outcome: data.outcome,
      operationId,
      kind: error?.kind || "protocol",
      details: error?.details ?? null,
    })
  }

  throw publicationProtocolError(operation, data)
}

const normalizeFailedHttpPublicationResponse = (
  error,
  operation,
  operationId
) => {
  const data = error?.response?.data
  if (
    isCanonicalPublicationResponse(data) &&
    data.result === 0 &&
    PUBLICATION_FAILURE_OUTCOMES.includes(data.outcome) &&
    !data.code.trim()
  ) {
    return normalizePublicationResponse(data, operation, operationId)
  }

  throw publicationTransportError(operation, operationId, error)
}

export class OneCSearchError extends Error {
  constructor({ message, kind }) {
    super(message)
    this.name = "OneCSearchError"
    this.kind = kind
  }
}

const SEARCH_ERROR_KINDS = [
  "configuration",
  "transport",
  "protocol",
  "catalog",
  "not_found",
  "provider",
]

const SEARCH_ERROR_MESSAGES = {
  configuration: "1C configuration unavailable",
  transport: "1C search transport failed",
  protocol: "Invalid 1C search response",
  catalog: "1C catalog validation failed",
  not_found: "Not found",
  provider: "1C provider rejected search",
}

const normalize1CSearchIdentifier = (value) => {
  if (typeof value === "string") {
    const normalized = value.trim()
    return normalized || null
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value)
  }
  return null
}

export const normalize1CSearchResponse = (data) => {
  if (!isPlainObject(data) || (data.result !== 0 && data.result !== 1)) {
    throw new OneCSearchError({
      message: SEARCH_ERROR_MESSAGES.protocol,
      kind: "protocol",
    })
  }

  if (data.result === 0) {
    const kind = SEARCH_ERROR_KINDS.includes(data.error_kind)
      ? data.error_kind
      : "provider"
    throw new OneCSearchError({
      message: SEARCH_ERROR_MESSAGES[kind],
      kind,
    })
  }

  const code = normalize1CSearchIdentifier(data.code)
  const barcode = normalize1CSearchIdentifier(data.barcode)
  if (code === null || barcode === null) {
    throw new OneCSearchError({
      message: SEARCH_ERROR_MESSAGES.protocol,
      kind: "protocol",
    })
  }

  return { ...data, code, barcode }
}

const PUBLICATION_STATUS_STATES = [
  "started",
  "indeterminate",
  "pending_card",
  "succeeded",
  "reconciled",
  "rejected",
  "not_sent",
]

export class OneCProductPublicationStatusError extends Error {
  constructor(message = "Invalid 1C publication status response") {
    super(message)
    this.name = "OneCProductPublicationStatusError"
    this.kind = "protocol"
  }
}

const normalize1CPublicationStatusResponse = (data, pType, itemId) => {
  if (!isPlainObject(data)) {
    throw new OneCProductPublicationStatusError()
  }
  const keys = Object.keys(data).sort()
  if (
    keys.length !== 4 ||
    keys.some(
      (key, index) =>
        key !==
        ["product_id", "product_type", "publication", "result"].sort()[index]
    ) ||
    data.result !== 1 ||
    data.product_type !== pType ||
    data.product_id !== itemId
  ) {
    throw new OneCProductPublicationStatusError()
  }

  if (data.publication === null) {
    return { pType, itemId, operationId: null, state: null }
  }
  if (!isPlainObject(data.publication)) {
    throw new OneCProductPublicationStatusError()
  }
  const publicationKeys = Object.keys(data.publication).sort()
  if (
    publicationKeys.length !== 2 ||
    publicationKeys[0] !== "operation_id" ||
    publicationKeys[1] !== "state" ||
    typeof data.publication.operation_id !== "string" ||
    !data.publication.operation_id.trim() ||
    data.publication.operation_id.trim().length > 36 ||
    !PUBLICATION_STATUS_STATES.includes(data.publication.state)
  ) {
    throw new OneCProductPublicationStatusError()
  }
  return {
    pType,
    itemId,
    operationId: data.publication.operation_id.trim(),
    state: data.publication.state,
  }
}

/**
 * Create catalog client with normalization and schema validation
 * @returns {Object} Catalog client with modules and items methods
 */
export function createCatalogClient() {
  const validatePositiveUniqueIds = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) {
      throw new Error(
        "Item IDs must be a non-empty array of unique positive safe integers"
      )
    }
    if (
      ids.some((id) => !Number.isSafeInteger(id) || id <= 0) ||
      new Set(ids).size !== ids.length
    ) {
      throw new Error(
        "Item IDs must be a non-empty array of unique positive safe integers"
      )
    }
    return ids
  }

  /**
   * Accept a mutation only when transport and response body both acknowledge it.
   * The shared interceptor resolves handled 401 responses, so callers cannot rely
   * on promise rejection alone before mutating local state or showing success.
   */
  const requireSemanticAcknowledgement = (
    response,
    operation,
    acknowledges
  ) => {
    const data = response?.data
    if (
      response?.__handled401 ||
      Number(response?.status) >= 400 ||
      data == null ||
      !acknowledges(data)
    ) {
      throw new Error(`${operation} was not acknowledged`)
    }
    return data
  }

  const requireExactMutationAcknowledgement = (
    response,
    operation,
    countField,
    requestedCount
  ) => {
    const data = response?.data
    const count = data?.[countField]
    if (
      response?.__handled401 ||
      Number(response?.status) >= 400 ||
      data?.result !== 1 ||
      !Number.isSafeInteger(count) ||
      count < 0 ||
      count !== requestedCount
    ) {
      throw new Error(`${operation} was not acknowledged`)
    }

    return data
  }

  // Modules list endpoint client
  const modulesListClient = createContractClient(
    api,
    "catalog/modules",
    ModulesListAnyResponseSchema,
    normalizeModulesList
  )

  return {
    /**
     * Get all catalog modules
     *
     * @returns {Promise<ModulesListResponse>} Normalized modules list
     * @returns {Array} returns - Array of normalized module objects
     *
     * @example
     * const modules = await catalogClient.getAllModules()
     * const activeModules = modules.filter(m => m.active === 1)
     */
    getAllModules: async () => {
      return modulesListClient.get("")
    },

    setProhibitedContent: async (productType, ids, action) => {
      if (!["lp", "cd"].includes(productType)) {
        throw new Error("Product type must be lp or cd")
      }
      if (!["set", "clear"].includes(action)) {
        throw new Error("Action must be set or clear")
      }
      const selectedIds = validatePositiveUniqueIds(ids)

      const response = await api.post("/products/prohibited_content_bulk", {
        product_type: productType,
        ids: selectedIds,
        action,
      })
      return requireSemanticAcknowledgement(
        response,
        "Publication mutation",
        (data) =>
          data?.status === "success" &&
          data?.product_type === productType &&
          data?.value === (action === "set" ? 1 : 0) &&
          Array.isArray(data?.ids) &&
          data.ids.length === selectedIds.length &&
          data.ids.every((id, index) => id === selectedIds[index])
      )
    },

    exportBoxData: async (boxId, productType, mode, ids = []) => {
      const normalizedBoxId = Number(boxId)
      if (!Number.isInteger(normalizedBoxId) || normalizedBoxId === 0) {
        throw new Error("A non-zero integer box ID is required")
      }
      if (!["lp", "cd", "acc_products"].includes(productType)) {
        throw new Error("Product type must be lp, cd, or acc_products")
      }
      if (!["subset", "box"].includes(mode)) {
        throw new Error("Export mode must be subset or box")
      }
      if (mode === "box" && (!Array.isArray(ids) || ids.length !== 0)) {
        throw new Error("Whole-box export must omit item IDs")
      }
      const selectedIds =
        mode === "subset" ? validatePositiveUniqueIds(ids) : []

      const exportModuleSegment =
        productType === "acc_products" ? "acc" : productType
      const response = await api.get(
        `/edit/new_api/export_data/box/${normalizedBoxId}/${exportModuleSegment}`,
        {
          params: mode === "subset" ? { ids: selectedIds } : undefined,
          responseType: "blob",
        }
      )
      return response.data
    },

    printLabels: async (payload) => {
      const { new: printLayout, ...printPayload } = payload ?? {}
      const response = await api.get("/print/labels", {
        params: {
          params: JSON.stringify(printPayload),
          new: printLayout,
        },
        responseType: "blob",
      })
      return response.data
    },

    /**
     * Get fields definition for a specific module
     *
     * @param {number} moduleId - Module ID to fetch fields for
     * @returns {Promise<ModuleFieldsResponse>} Normalized module fields array
     * @returns {Array} returns - Array of field definitions
     * @throws {Error} When moduleId is not provided
     *
     * @example
     * const fields = await catalogClient.getModuleFields(1)
     * const requiredFields = fields.filter(f => f.required)
     */
    getModuleFields: async (moduleId) => {
      if (!moduleId) {
        throw new Error("Module ID is required for fetching module fields")
      }
      // Use contractRequest directly for dynamic endpoint
      return contractRequest({
        api,
        endpoint: `catalog/modules/${moduleId}/fields`,
        method: "GET",
        params: undefined,
        schema: ModuleFieldsResponseSchema,
        normalizer: normalizeModuleFields,
      })
    },

    /**
     * Get all items for a specific module
     *
     * @param {number} moduleId - Module ID to fetch items for
     * @returns {Promise<ModuleItemsResponse>} Normalized module items array
     * @returns {Array} returns - Array of normalized item objects
     * @throws {Error} When moduleId is not provided
     *
     * @example
     * const items = await catalogClient.getModuleItems(1)
     * const activeItems = items.filter(i => i.active === 1)
     */
    getModuleItems: async (moduleId) => {
      if (!moduleId) {
        throw new Error("Module ID is required for fetching module items")
      }
      // Use contractRequest directly for dynamic endpoint
      return contractRequest({
        api,
        endpoint: `catalog/modules/${moduleId}/items`,
        method: "GET",
        params: undefined,
        schema: ModuleItemsResponseSchema,
        normalizer: normalizeModuleItems,
      })
    },

    /**
     * Get single item by ID from a specific module
     *
     * @param {number} moduleId - Module ID
     * @param {number} itemId - Item ID to fetch
     * @returns {Promise<ModuleItemResponse>} Normalized item object
     * @returns {Object} returns - Item object with all fields
     * @throws {Error} When moduleId or itemId is not provided
     *
     * @example
     * const item = await catalogClient.getModuleItem(1, 5)
     * console.log('Editing item:', item.name)
     */
    getModuleItem: async (moduleId, itemId) => {
      if (!moduleId) {
        throw new Error("Module ID is required for fetching an item")
      }
      if (!itemId) {
        throw new Error("Item ID is required for fetching an item")
      }
      // Use contractRequest directly for dynamic endpoint
      return contractRequest({
        api,
        endpoint: `catalog/modules/${moduleId}/items/${itemId}`,
        method: "GET",
        params: undefined,
        schema: ModuleItemResponseSchema,
        normalizer: normalizeModuleItem,
      })
    },

    /**
     * Create a new item in a specific module
     *
     * @param {number} moduleId - Module ID to create item in
     * @param {Object} itemData - Item creation data
     * @param {string} itemData.name - Item name (required)
     * @param {number} [itemData.active] - Active status (default: 1)
     * @param {number} [itemData.order_num] - Display order
     * @returns {Promise<ModuleItemResponse>} Created item object
     * @returns {Object} returns - Created item with all fields
     * @throws {Error} When moduleId is not provided
     *
     * @example
     * const newItem = await catalogClient.createItem(1, {
     *   name: 'New Item',
     *   active: 1
     * })
     */
    createItem: async (moduleId, itemData) => {
      if (!moduleId) {
        throw new Error("Module ID is required for creating an item")
      }
      // Use contractRequest directly for dynamic endpoint
      return contractRequest({
        api,
        endpoint: `catalog/modules/${moduleId}/items`,
        method: "POST",
        schema: ModuleItemOperationResponseSchema,
        data: itemData,
        normalizer: normalizeModuleItem,
      })
    },

    /**
     * Update an existing item in a specific module
     *
     * @param {number} moduleId - Module ID
     * @param {number} itemId - Item ID to update
     * @param {Object} itemData - Item update data (all fields optional)
     * @param {string} [itemData.name] - Item name
     * @param {number} [itemData.active] - Active status
     * @param {number} [itemData.order_num] - Display order
     * @returns {Promise<ModuleItemResponse>} Updated item object
     * @returns {Object} returns - Updated item with all fields
     * @throws {Error} When moduleId or itemId is not provided
     *
     * @example
     * const updatedItem = await catalogClient.updateItem(1, 5, {
     *   name: 'Updated Item'
     * })
     */
    updateItem: async (moduleId, itemId, itemData) => {
      if (!moduleId) {
        throw new Error("Module ID is required for updating an item")
      }
      if (!itemId) {
        throw new Error("Item ID is required for updating an item")
      }
      // Use contractRequest directly for dynamic endpoint
      return contractRequest({
        api,
        endpoint: `catalog/modules/${moduleId}/items/${itemId}`,
        method: "PUT",
        schema: ModuleItemOperationResponseSchema,
        data: itemData,
        normalizer: normalizeModuleItem,
      })
    },

    /**
     * Delete an item from a specific module
     *
     * @param {number} moduleId - Module ID
     * @param {number} itemId - Item ID to delete
     * @returns {Promise<DeleteResponse>} Delete response
     * @returns {Object} returns - Response object with result and optional message
     * @returns {string} returns.result - Result status ('success' or error code)
     * @returns {string} [returns.message] - Optional message
     * @throws {Error} When moduleId or itemId is not provided
     *
     * @example
     * const response = await catalogClient.deleteItem(1, 5)
     * if (response.result === 'success') {
     *   console.log('Item deleted:', response.message)
     * }
     */
    deleteItem: async (moduleId, itemId) => {
      if (!moduleId) {
        throw new Error("Module ID is required for deleting an item")
      }
      if (!itemId) {
        throw new Error("Item ID is required for deleting an item")
      }
      // Use contractRequest directly for dynamic endpoint
      return contractRequest({
        api,
        endpoint: `catalog/modules/${moduleId}/items/${itemId}`,
        method: "DELETE",
        schema: ModuleDeleteResponseSchema,
        normalizer: normalizeDeleteResponse,
      })
    },

    /**
     * Delete multiple items from a specific module
     *
     * @param {number} moduleId - Module ID
     * @param {Array<number>} itemIds - Array of item IDs to delete
     * @returns {Promise<DeleteResponse>} Delete response
     * @returns {Object} returns - Response object with result and optional message
     * @throws {Error} When moduleId is not provided or itemIds is empty
     *
     * @example
     * const response = await catalogClient.deleteItems(1, [5, 6, 7])
     * if (response.result === 'success') {
     *   console.log('Items deleted')
     * }
     */
    deleteItems: async (moduleId, itemIds) => {
      if (!moduleId) {
        throw new Error("Module ID is required for deleting items")
      }
      if (!Array.isArray(itemIds) || itemIds.length === 0) {
        throw new Error("Item IDs array is required and must not be empty")
      }
      // Use contractRequest directly for dynamic endpoint with data in DELETE
      return contractRequest({
        api,
        endpoint: `catalog/modules/${moduleId}/items`,
        method: "DELETE",
        schema: ModuleDeleteResponseSchema,
        data: { ids: itemIds },
        normalizer: normalizeDeleteResponse,
      })
    },

    /**
     * Toggle visibility (active status) for multiple items
     *
     * @param {number} moduleId - Module ID
     * @param {Array<number>} itemIds - Array of item IDs to toggle
     * @param {number} active - Active status to set (0 or 1)
     * @returns {Promise<ToggleResponse>} Toggle response
     * @returns {Object} returns - Response object with result and optional message
     * @throws {Error} When moduleId is not provided or itemIds is empty
     *
     * @example
     * // Activate items
     * const response = await catalogClient.toggleItemsVisibility(1, [5, 6], 1)
     * // Deactivate items
     * const response2 = await catalogClient.toggleItemsVisibility(1, [7, 8], 0)
     */
    toggleItemsVisibility: async (moduleId, itemIds, active) => {
      if (!moduleId) {
        throw new Error("Module ID is required for toggling item visibility")
      }
      if (!Array.isArray(itemIds) || itemIds.length === 0) {
        throw new Error("Item IDs array is required and must not be empty")
      }
      if (typeof active !== "number") {
        throw new Error("Active status must be a number (0 or 1)")
      }
      // Use contractRequest directly for dynamic endpoint
      return contractRequest({
        api,
        endpoint: `catalog/modules/${moduleId}/items`,
        method: "PUT",
        schema: ModuleToggleResponseSchema,
        data: { ids: itemIds, active },
        normalizer: normalizeToggleResponse,
      })
    },

    /**
     * Refresh/reorder items in a specific module
     *
     * @param {number} moduleId - Module ID
     * @param {Array<Object>} items - Array of items with updated order
     * @param {number} items[].id - Item ID
     * @param {number} items[].order_num - New order position
     * @returns {Promise<ModuleItemsResponse>} Updated items array
     * @returns {Array} returns - Array of updated item objects
     * @throws {Error} When moduleId is not provided or items is empty
     *
     * @example
     * const response = await catalogClient.refreshItems(1, [
     *   { id: 5, order_num: 1 },
     *   { id: 6, order_num: 2 },
     *   { id: 7, order_num: 3 }
     * ])
     */
    refreshItems: async (moduleId, items) => {
      if (!moduleId) {
        throw new Error("Module ID is required for refreshing items")
      }
      if (!Array.isArray(items) || items.length === 0) {
        throw new Error("Items array is required and must not be empty")
      }
      // Use contractRequest directly for dynamic endpoint
      return contractRequest({
        api,
        endpoint: `catalog/modules/${moduleId}/items`,
        method: "PUT",
        schema: ModuleItemsResponseSchema,
        data: { items },
        normalizer: normalizeModuleItems,
      })
    },

    /**
     * Duplicate an entire module with its items
     *
     * @param {number} moduleId - Module ID to duplicate
     * @param {string} newName - Name for the duplicated module
     * @returns {Promise<DuplicateResponse>} Duplicate response
     * @returns {Object} returns - Response object with result, new module ID, and optional message
     * @returns {string} returns.result - Result status ('success' or error code)
     * @returns {number} returns.moduleId - New module ID
     * @returns {string} [returns.message] - Optional message
     * @throws {Error} When moduleId is not provided
     *
     * @example
     * const response = await catalogClient.duplicateModule(1, 'New Module Copy')
     * if (response.result === 'success') {
     *   console.log('Module duplicated with ID:', response.moduleId)
     * }
     */
    duplicateModule: async (moduleId, newName) => {
      if (!moduleId) {
        throw new Error("Module ID is required for duplicating a module")
      }
      // Use contractRequest directly for dynamic endpoint
      return contractRequest({
        api,
        endpoint: `catalog/modules/${moduleId}/duplicate`,
        method: "POST",
        schema: ModuleDuplicateResponseSchema,
        data: { name: newName },
        normalizer: normalizeDuplicateResponse,
      })
    },

    /**
     * Export module data as an authenticated CSV download payload.
     *
     * @param {number} moduleId - Module ID to export
     * @returns {Promise<{blob: Blob, filename: string}>} CSV blob and safe filename
     * @throws {Error} When moduleId is not provided or the response is not CSV
     */
    exportModule: async (moduleId) => {
      if (!moduleId) {
        throw new Error("Module ID is required for exporting a module")
      }

      const response = await api.get(`catalog/modules/${moduleId}/export`, {
        responseType: "blob",
      })
      const blob = response?.data
      const contentType = getResponseHeader(response, "content-type")
      if (
        response?.__handled401 ||
        Number(response?.status) >= 400 ||
        !(blob instanceof Blob) ||
        !isCsvContentType(contentType) ||
        !isCsvContentType(blob.type)
      ) {
        throw new Error("Module export did not return a CSV Blob")
      }

      return {
        blob,
        filename: getExportFilename(
          getResponseHeader(response, "content-disposition"),
          moduleId
        ),
      }
    },

    /**
     * Get module items with filters and pagination (legacy endpoint)
     *
     * @param {number} moduleId - Module ID
     * @param {Object} options - Query options
     * @param {Array<string>} options.searchFields - Fields to search in
     * @param {string} options.strFilter - Search filter string
     * @param {number} options.page - Page number
     * @param {number} options.per_page - Items per page
     * @param {string} options.sortField - Field to sort by
     * @param {string} options.sortDir - Sort direction (ASC/DESC)
     * @param {Object} options.moduleFilters - Module-specific filters
     * @param {Object} options.moduleCustomFilters - Custom filters
     * @param {Object} options.signal - AbortSignal for cancellation
     * @param {string} [options.requestId] - Nonempty caller-owned X-Request-ID; omitted IDs are generated by transport
     * @returns {Promise<Object>} Response with items array and total count
     * @returns {Array} returns.items - Items array
     * @returns {number} returns.total - Total item count
     * @returns {boolean} returns.totalCapped - True only when the server reports a capped result; absent means false.
     * @throws {Error} When moduleId is not provided
     */
    getModuleItemsWithFilters: async (moduleId, options = {}) => {
      if (!moduleId) {
        throw new Error("Module ID is required for fetching module items")
      }
      const {
        searchFields,
        strFilter,
        page,
        per_page,
        sortField,
        sortDir,
        moduleFilters,
        moduleCustomFilters,
        signal,
        requestId,
      } = options

      // Direct API call for legacy endpoint with POST and filters
      const response = await api.post(
        `modules/items/${moduleId}`,
        {
          searchFields: searchFields || [],
          strFilter: strFilter || "",
          page: page || 1,
          per_page: per_page || 100,
          sortField: sortField || "id",
          sortDir: sortDir || "DESC",
          moduleFilters: moduleFilters || {},
          moduleCustomFilters: moduleCustomFilters || {},
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(typeof requestId === "string" && requestId.length > 0
              ? { "X-Request-ID": requestId }
              : {}),
          },
          signal,
        }
      )

      if (response?.__canceled === true) {
        return { __canceled: true }
      }

      const data = response?.data
      if (
        !isPlainObject(data) ||
        !Object.prototype.hasOwnProperty.call(data, "items") ||
        !Array.isArray(data.items)
      ) {
        throw new Error(
          "Module items response must be a plain object with an items array"
        )
      }

      const isLpCdModule = [112, 115].includes(Number(moduleId))

      return {
        items: data.items.map((item) => {
          const mItemId = normalizePositiveSafeItemId(item?.m_item_id)
          const normalizedItem = isLpCdModule
            ? normalizeLpCdListRowDisplayFields(item)
            : item

          return {
            ...normalizedItem,
            m_item_id: mItemId,
            selected: false,
            qtyForPack: item.m_item_pack_qty,
          }
        }),
        total: data.total || 0,
        totalCapped: data.totalCapped === true,
      }
    },

    /**
     * Ask the backend to cancel the current user's module-items SQL request.
     *
     * @param {string} requestId - Nonempty request identity (32 hex per backend contract).
     * @returns {Promise<Object|undefined>} Backend response data with cancelled status.
     * @throws {Error} When requestId is empty; transport failures reject the promise.
     */
    cancelModuleItemsRequest: async (requestId) => {
      if (typeof requestId !== "string" || requestId.length === 0) {
        throw new Error("Request ID is required for cancelling module items")
      }
      const response = await api.post(
        "modules/items/cancel",
        { requestId },
        { headers: { "Content-Type": "application/json" } }
      )
      return response?.data
    },

    /**
     * Get module fields from legacy endpoint
     *
     * @param {number} moduleId - Module ID
     * @returns {Promise<Array>} Array of field definitions
     * @throws {Error} When moduleId is not provided
     */
    getModuleFieldsLegacy: async (moduleId) => {
      if (!moduleId) {
        throw new Error("Module ID is required for fetching module fields")
      }
      const response = await api.get(`modules/fields/${moduleId}`)
      if (!Array.isArray(response.data)) {
        throw new TypeError("Module fields response must be an array")
      }
      return normalizeModuleFilterFields(response.data, { moduleId })
    },

    /**
     * Get dictionary data for linked fields
     *
     * @param {string} moduleLinkName - Module name for dictionary
     * @returns {Promise<Array>} Array of dictionary items
     * @throws {Error} When moduleLinkName is not provided
     */
    getDictionary: async (moduleLinkName) => {
      if (!moduleLinkName) {
        throw new Error("Module link name is required for fetching dictionary")
      }
      const response = await api.get(`dict/${moduleLinkName}`)
      return response.data || []
    },

    /**
     * Delete multiple items (legacy endpoint)
     *
     * @param {number} moduleId - Module ID
     * @param {Array<number>} itemIds - Array of item IDs to delete
     * @returns {Promise<Object>} Response object
     * @throws {Error} When moduleId is not provided or itemIds is empty
     */
    deleteItemsLegacy: async (moduleId, itemIds) => {
      if (!moduleId) {
        throw new Error("Module ID is required for deleting items")
      }
      const selectedIds = validatePositiveUniqueIds(itemIds)
      const response = await api.post("modules/delete_items", {
        module_id: moduleId,
        id: selectedIds,
      })
      return requireExactMutationAcknowledgement(
        response,
        "Delete mutation",
        "deleted_count",
        selectedIds.length
      )
    },

    /**
     * Toggle visibility for multiple items (legacy endpoint)
     *
     * @param {number} moduleId - Module ID
     * @param {Array<number>} itemIds - Array of item IDs
     * @param {string} action - Action to perform ("show" or "hide")
     * @returns {Promise<Object>} Response object
     * @throws {Error} When moduleId is not provided or itemIds is empty
     */
    toggleItemsVisibilityLegacy: async (moduleId, itemIds, action) => {
      if (!moduleId) {
        throw new Error("Module ID is required for toggling item visibility")
      }
      const selectedIds = validatePositiveUniqueIds(itemIds)
      if (!["show", "hide"].includes(action)) {
        throw new Error("Action must be 'show' or 'hide'")
      }
      const response = await api.post("modules/items_show_hide", {
        module_id: moduleId,
        id: selectedIds,
        action,
      })
      return requireExactMutationAcknowledgement(
        response,
        "Visibility mutation",
        "updated_count",
        selectedIds.length
      )
    },

    /**
     * Refresh multiple items (legacy endpoint)
     *
     * @param {number} moduleId - Module ID
     * @param {Array<number>} itemIds - Array of item IDs to refresh
     * @param {string} mode - Refresh mode
     * @returns {Promise<Object>} Response object
     * @throws {Error} When moduleId is not provided or itemIds is empty
     */
    refreshItemsLegacy: async (moduleId, itemIds, mode) => {
      if (!moduleId) {
        throw new Error("Module ID is required for refreshing items")
      }
      const selectedIds = validatePositiveUniqueIds(itemIds)
      const response = await api.post("modules/refresh_items", {
        module_id: moduleId,
        id: selectedIds,
        mode,
      })
      return requireExactMutationAcknowledgement(
        response,
        "Refresh mutation",
        "updated_count",
        selectedIds.length
      )
    },

    /**
     * Save items sort order (legacy endpoint)
     *
     * @param {number} moduleId - Module ID
     * @param {Array<Object>} itemsOrder - Array of items with new order
     * @param {number} itemsOrder[].id - Item ID
     * @param {number} itemsOrder[].index - New order index
     * @returns {Promise<Object>} Response object
     * @throws {Error} When moduleId is not provided or itemsOrder is empty
     */
    saveItemsOrderLegacy: async (moduleId, itemsOrder) => {
      if (!moduleId) {
        throw new Error("Module ID is required for saving items order")
      }
      if (!Array.isArray(itemsOrder) || itemsOrder.length === 0) {
        throw new Error("Items order array is required and must not be empty")
      }
      const response = await api.post(
        `modules/items/${moduleId}/sort`,
        { itemsOrder },
        { headers: { "Content-Type": "application/json" } }
      )
      return response.data
    },

    /**
     * Delete single item (legacy endpoint using GET)
     *
     * @param {number} moduleId - Module ID
     * @param {number} itemId - Item ID to delete
     * @returns {Promise<Object>} Response object
     * @throws {Error} When moduleId or itemId is not provided
     */
    deleteItemLegacy: async (moduleId, itemId) => {
      if (!moduleId) {
        throw new Error("Module ID is required for deleting an item")
      }
      if (!itemId) {
        throw new Error("Item ID is required for deleting an item")
      }
      const response = await api.get(
        `modules/delete_item/${moduleId}/${itemId}`
      )
      return response.data
    },

    /**
     * Get shop visits closed dates
     *
     * @returns {Promise<Object>} Response with dates array
     * @returns {Array<number>} returns.dates - Array of Unix timestamps
     */
    getShopVisitsClosed: async () => {
      const response = await api.get("get_shop_visits_closed")
      return response.data || {}
    },

    /**
     * Save shop visits closed dates
     *
     * @param {Array<Date>} dates - Array of closed dates
     * @returns {Promise<Object>} Response object
     */
    saveShopVisitsClosed: async (dates) => {
      const response = await api.post("custom/shop_visits_close", {
        dates,
      })
      return response.data
    },

    /**
     * Get filled box numbers for a module
     *
     * @param {string} moduleName - Module alias (lp, cd, acc_products)
     * @returns {Promise<Array<number>>} Sorted unique filled box numbers
     */
    getFilledBoxes: async (moduleName) => {
      if (!moduleName) {
        throw new Error("Module name is required for fetching filled boxes")
      }

      return contractRequest({
        api,
        endpoint: `custom/filled_boxes/${moduleName}`,
        method: "GET",
        params: undefined,
        schema: CustomFilledBoxesResponseSchema,
        normalizer: normalizeFilledBoxes,
      })
    },

    /**
     * Move multiple items to a box (custom endpoint)
     *
     * @param {string} moduleName - Module name
     * @param {Array<number>} itemIds - Array of item IDs
     * @param {number} boxNum - Box number
     * @returns {Promise<Object>} Response object
     * @throws {Error} When moduleName is not provided or itemIds is empty
     */
    moveManyToBox: async (moduleName, itemIds, boxNum) => {
      if (!moduleName) {
        throw new Error("Module name is required for moving items to box")
      }
      const selectedIds = validatePositiveUniqueIds(itemIds)
      const response = await api.post("custom/move_many_to_box", {
        module_name: moduleName,
        id: selectedIds,
        box: boxNum,
      })
      return requireExactMutationAcknowledgement(
        response,
        "Move mutation",
        "moved_count",
        selectedIds.length
      )
    },

    /**
     * Get module item for editing (legacy endpoint).
     *
     * @precondition moduleId and itemId must be provided.
     * @postcondition For LP/CD modules (115/112), returned payload has album-only title text and canonical LP-copy dates before form hydration.
     * @postcondition Non-LP/CD modules return backend payload unchanged.
     * @mutates none
     * @throws {Error} When moduleId or itemId is not provided
     */
    getModuleItemForEdit: async (moduleId, itemId) => {
      if (!moduleId) {
        throw new Error("Module ID is required for fetching an item")
      }
      if (!itemId) {
        throw new Error("Item ID is required for fetching an item")
      }
      const response = await api.get(`modules/${moduleId}/get/${itemId}`)
      return normalizeModuleItemForEditPayload(response.data, {
        moduleId,
      })
    },

    /**
     * Create new module item (legacy endpoint)
     *
     * @param {number} moduleId - Module ID
     * @param {Object} itemData - Item data
     * @param {boolean} hasFileObjects - Whether itemData contains File/Blob objects
     * @returns {Promise<Object>} Response object
     * @throws {Error} When moduleId is not provided
     */
    createModuleItem: async (
      moduleId,
      itemData,
      hasFileObjects = false,
      actionPolicy = null
    ) => {
      if (!moduleId) {
        throw new Error("Module ID is required for creating an item")
      }
      if (isExistingNumericLink(itemData)) {
        return itemData
      }
      if (
        isNewLinkedItem(itemData) &&
        (!actionPolicy ||
          typeof actionPolicy.can !== "function" ||
          actionPolicy.can("module.item.create-linked", { moduleId }) !== true)
      ) {
        throw new Error(
          "Creating a linked item requires target-module create/edit authority"
        )
      }

      // LLM:METADATA
      // :topology: [Domain:ModuleManagement | API | CreateItem]
      // :relates-to: uses: "api", "ModulesController.modules_new_item"
      // :rationale: "Fixed endpoint path to match backend route. Using relative path with api instance (baseURL=/api) to reach /api/modules/new_item/:moduleId"
      // :contract: pre: "moduleId provided, itemData valid", post: "Item created or error thrown"
      // :decision_cache: "Fixed path from 'modules/new_item/:id' to match canonical route pattern [FIX-CREATE-ITEM-001]"
      // LLM:END

      const normalizedItemData = normalizeModuleItemForSavePayload(itemData)

      // If there are File objects, send as FormData (required for image field uploads)
      if (hasFileObjects) {
        const formData = new FormData()
        formData.append("module_id", moduleId)

        Object.entries(normalizedItemData).forEach(([key, value]) => {
          if (value instanceof File || value instanceof Blob) {
            formData.append(key, value)
          } else if (value !== undefined) {
            formData.append(key, value === null ? "" : String(value))
          }
        })

        // LLM:METADATA
        // :topology: [Domain:ModuleManagement | API | FormDataUpload]
        // :relates-to: uses: "api.post", "ModulesController.modules_new_item"
        // :rationale: "Use relative path for FormData upload. api instance has baseURL=/api, so this becomes POST /api/modules/new_item/115"
        // :contract: pre: "formData constructed", post: "Backend receives multipart/form-data", invariant: "No /api prefix in url (baseURL already includes it)"
        // :decision_cache: "Keep relative path to avoid double /api prefix [API-ROUTE-001]"
        // LLM:END
        const response = await api.post(
          `modules/new_item/${moduleId}`,
          formData
        )
        return response.data
      }

      // LLM:METADATA
      // :topology: [Domain:ModuleManagement | API | JSONUpload]
      // :relates-to: uses: "api.post", "ModulesController.modules_new_item"
      // :rationale: "Use relative path for JSON upload. api instance has baseURL=/api, so this becomes POST /api/modules/new_item/115"
      // :contract: pre: "itemData provided", post: "Backend receives JSON", invariant: "No /api prefix in url (baseURL already includes it)"
      // :decision_cache: "Keep relative path to avoid double /api prefix [API-ROUTE-001]"
      // LLM:END
      const response = await api.post(`modules/new_item/${moduleId}`, {
        ...normalizedItemData,
      })
      return response.data
    },

    /**
     * Update module item (legacy endpoint)
     *
     * @param {number} moduleId - Module ID
     * @param {number} itemId - Item ID
     * @param {Object} itemData - Item data
     * @param {boolean} hasFileObjects - Whether itemData contains File/Blob objects
     * @returns {Promise<Object>} Response object
     * @throws {Error} When moduleId or itemId is not provided
     */
    updateModuleItem: async (
      moduleId,
      itemId,
      itemData,
      hasFileObjects = false
    ) => {
      if (!moduleId) {
        throw new Error("Module ID is required for updating an item")
      }
      if (!itemId) {
        throw new Error("Item ID is required for updating an item")
      }

      const normalizedItemData = normalizeModuleItemForSavePayload(itemData)

      // If there are File objects, send as FormData (required for image field uploads)
      if (hasFileObjects) {
        const formData = new FormData()
        formData.append("module_id", moduleId)
        formData.append("item_id", itemId)

        Object.entries(normalizedItemData).forEach(([key, value]) => {
          if (value instanceof File || value instanceof Blob) {
            formData.append(key, value)
          } else if (value !== undefined) {
            formData.append(key, value === null ? "" : String(value))
          }
        })

        const response = await api.post("modules/edit_item", formData)
        return response.data
      }

      const response = await api.post("modules/edit_item", {
        module_id: moduleId,
        item_id: itemId,
        ...normalizedItemData,
      })
      return response.data
    },

    /**
     * Delete module item (legacy endpoint with POST)
     *
     * @param {number} moduleId - Module ID
     * @param {number} itemId - Item ID
     * @param {number} userId - User ID
     * @returns {Promise<Object>} Response object
     * @throws {Error} When moduleId or itemId is not provided
     */
    deleteModuleItem: async (moduleId, itemId, userId) => {
      if (!moduleId) {
        throw new Error("Module ID is required for deleting an item")
      }
      if (!itemId) {
        throw new Error("Item ID is required for deleting an item")
      }
      const response = await api.post(
        `modules/delete_item/${moduleId}/${itemId}`,
        {
          user_id: userId,
        }
      )
      return response.data
    },

    /**
     * Search YouTube for video by artist and album
     *
     * @param {string} artist - Artist name
     * @param {string} album - Album name
     * @returns {Promise<Object>} Response with y_code
     * @throws {Error} When artist or album is not provided
     */
    searchYoutube: async (artist, album) => {
      if (!artist) {
        throw new Error("Artist is required for searching YouTube")
      }
      if (!album) {
        throw new Error("Album is required for searching YouTube")
      }
      const response = await api.post("custom/search_youtube", {
        artist,
        album,
      })
      return response.data
    },

    /**
     * Get artist last styles
     *
     * @param {number} artistId - Artist ID
     * @param {string} productType - Product type (lp, cd, ac)
     * @returns {Promise<Object>} Response with styles array
     * @throws {Error} When artistId is not provided
     */
    getArtistStyles: async (artistId, productType) => {
      if (!artistId) {
        throw new Error("Artist ID is required for fetching styles")
      }
      const response = await api.post("custom/artist_last_styles", {
        artist_id: artistId,
        product_type: productType,
      })
      return response.data
    },

    /**
     * Compose 1C product name
     *
     * @param {Object} params - Product parameters
     * @param {number} [params.itemId] - Item ID (optional, for saved items)
     * @param {string} params.pType - Product type (lp, cd, ac)
     * @param {Object} [params.draft] - Draft payload (optional, for unsaved items)
     * @returns {Promise<Object>} Response with composed name
     * @throws {Error} When neither itemId nor draft is provided
     */
    compose1CName: async ({ itemId, pType, draft }) => {
      // LLM:METADATA
      // :topology: [Domain:Integration_1C | Role:Name_Compose | Pattern:Dual_Path_Contract]
      // :relates-to: uses: "api", required-by: "CustomController::product_1c_compose"
      // :rationale: "Backend accepts both itemId (saved items) and draft (unsaved items) for 1C name composition. Send both when available; backend will use appropriate path. At least one source must be provided."
      // :contract: pre: "itemId or draft provided", post: "Backend receives item_id (if available) + type + draft JSON string (if provided)", invariant: "At least one source is sent"
      // :decision_cache: "Added draft parameter support for unsaved item compose [FE-1C-COMPOSE-DRAFT-001]; Keep itemId optional for backward compatibility [BACKWARD-COMPAT-001]"
      // LLM:END

      if (!itemId && !draft) {
        throw new Error(
          "Either itemId (for saved items) or draft (for unsaved items) is required for composing 1C name"
        )
      }

      const formData = new FormData()
      // Use canonical parameter name 'type' (not p_type)
      formData.append("type", pType || "lp")

      // Include item_id if available (saved item path)
      if (itemId) {
        formData.append("item_id", itemId)
      }

      // Include draft JSON string if provided (unsaved item path)
      if (draft) {
        formData.append("draft", JSON.stringify(draft))
      }

      const response = await api.post("custom/product_1c_compose", formData)
      return response.data
    },

    /**
     * Search product in 1C
     *
     * @param {{name: string, pType: "lp"|"cd"|"ac", folderId?: string|number}} options - 1C search payload
     * @returns {Promise<Object>} Response with 1C data
     * @throws {Error} When name or product type is invalid
     */
    searchIn1C: async (options) => {
      if (!options || typeof options !== "object" || Array.isArray(options)) {
        throw new Error("Search options are required for searching in 1C")
      }

      const { name, pType } = options
      if (typeof name !== "string" || !name.trim()) {
        throw new Error("Name is required for searching in 1C")
      }
      if (!["lp", "cd", "ac"].includes(pType)) {
        throw new Error("Product type must be one of lp, cd, ac")
      }

      // LLM:METADATA
      // :topology: [Domain:Integration_1C | Role:Search | Pattern:API_Call]
      // :relates-to: uses: "api", required-by: "CustomController::product_to_1c"
      // :rationale: "Backend expects name and canonical product type via POST. Use FormData for PHP \$this->input->post() compatibility (FE-1C-SEARCH-001)."
      // :contract: pre: "nonblank name and canonical pType provided", post: "Backend returns 1C data or error", invariant: "search sends only name and type; catalog belongs to save/export validation"
      // :decision_cache: "Restored the legacy name-only search seam; folder_id remains owned by save/publication [FE-1C-SEARCH-001]"
      // LLM:END
      const formData = new FormData()
      formData.append("name", name)
      formData.append("type", pType)

      const response = await api.post("custom/product_to_1c", formData)
      return normalize1CSearchResponse(response.data)
    },

    get1CPublicationStatus: async ({ itemId, pType } = {}) => {
      const normalizedItemId = normalizePositiveSafeItemId(itemId)
      if (!["lp", "cd", "ac"].includes(pType)) {
        throw new Error("Product type must be one of lp, cd, ac")
      }
      const formData = new FormData()
      formData.append("product_id", normalizedItemId)
      formData.append("p_type", pType)
      const response = await api.post("custom/product_publication_1c", formData)
      return normalize1CPublicationStatusResponse(
        response?.data,
        pType,
        normalizedItemId
      )
    },

    /**
     * Save product to 1C
     *
     * @param {Object} params - Product parameters
     * @param {number|string} params.itemId - Positive item ID for a saved card
     * @param {string} params.pType - Product type (lp, cd, ac)
     * @param {string} params.supplier - Supplier
     * @param {number} params.stock2 - Stock 2 flag
     * @param {string} params.preparedName - Prepared 1C name (required authoritative value)
     * @param {string} params.barcode - Barcode
     * @param {string} params.folderId - Folder ID
     * @returns {Promise<Object>} Response with saved data
     * @throws {Error} When itemId or preparedName is not provided
     */
    saveTo1C: async ({
      itemId,
      pType,
      supplier,
      stock2,
      preparedName,
      barcode,
      folderId,
    }) => {
      const normalizedItemId = normalizePositiveSafeItemId(itemId)
      const normalizedPreparedName = String(preparedName || "").trim()
      if (!normalizedPreparedName) {
        throw new Error("Prepared name is required for saving to 1C")
      }
      if (!["lp", "cd", "ac"].includes(pType)) {
        throw new Error("Product type must be one of lp, cd, ac")
      }
      const normalizedFolderId = normalizePositiveFolderId(folderId)
      // AC keeps the legacy optionality: a folder is only required for CD.
      if (pType === "cd" && !normalizedFolderId) {
        throw new Error("Folder ID is required for saving to 1C")
      }

      // LLM:METADATA
      // :topology: [Domain:Integration_1C | Role:Save | Pattern:Prepared_Name_Authority]
      // :relates-to: uses: "api", required-by: "CustomController::product_save_1c"
      // :rationale: "The backend product fence and card persistence are authoritative for saved cards; the Editor supplies only the saved card identity and current 1C inputs."
      // :contract: pre: "positive itemId and preparedName provided", post: "Backend receives the saved-card identity and canonical 1C inputs", invariant: "Unsaved cards fail closed before transport"
      // LLM:END
      const formData = new FormData()
      formData.append("id", normalizedItemId)
      formData.append("item_id", normalizedItemId)
      formData.append("product_id", normalizedItemId)
      formData.append("p_type", pType || "")
      formData.append("supplier", supplier || "")
      formData.append("stock_2", stock2 ? "1" : "0")
      formData.append("str", normalizedPreparedName)
      formData.append("barcode", barcode || "")
      formData.append("folder_id", normalizedFolderId ?? "")

      let response
      try {
        response = await api.post("custom/product_save_1c", formData)
      } catch (error) {
        return normalizeFailedHttpPublicationResponse(error, "save", null)
      }
      return normalizePublicationResponse(response?.data, "save")
    },

    /**
     * Explicitly reconcile an indeterminate 1C publication operation.
     *
     * @param {string} operationId - Operation identity returned by the backend
     * @param {"lp"|"cd"|"ac"} pType - Product type
     * @returns {Promise<{operationId: string, code: string, barcode: string}>}
     */
    reconcile1CPublication: async (operationId, pType) => {
      const normalizedOperationId =
        typeof operationId === "string" ? operationId.trim() : ""
      if (!normalizedOperationId) {
        throw new Error(
          "Operation ID is required for reconciling 1C publication"
        )
      }
      if (!["lp", "cd", "ac"].includes(pType)) {
        throw new Error("Product type must be one of lp, cd, ac")
      }

      const formData = new FormData()
      formData.append("operation_id", normalizedOperationId)
      formData.append("p_type", pType)

      let response
      try {
        response = await api.post("custom/product_reconcile_1c", formData)
      } catch (error) {
        return normalizeFailedHttpPublicationResponse(
          error,
          "reconcile",
          normalizedOperationId
        )
      }
      return normalizePublicationResponse(
        response?.data,
        "reconcile",
        normalizedOperationId
      )
    },
  }
}

// Singleton instance for direct import
export const catalogClient = createCatalogClient()
